import { ChevronsUpDown, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useGetHotelTeamForRecipientsQuery } from '../gql/_gen_/report-subscriptions.gql';

type Props = {
  brandCode: string;
  value?: string[] | null;
  onChange: (nextIds: string[]) => void;
  disabled?: boolean;
};

export function HotelTeamRecipientsSelect({ brandCode, value, onChange, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const normalizedBrandCode = brandCode.trim().toUpperCase();
  const canQuery = normalizedBrandCode.length === 5;

  const { data, loading, error } = useGetHotelTeamForRecipientsQuery({
    variables: { brandCode: normalizedBrandCode },
    skip: !canQuery,
  });

  const options = data?.getHotelTeam ?? [];
  const selectedIds = value ?? [];

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filtered = options.filter((m): m is NonNullable<typeof m> & { id: string } => {
      if (!m?.id) return false;
      if (!normalizedSearch) return true;
      const name = (m.name ?? '').toLowerCase();
      const email = (m.email ?? '').toLowerCase();
      return name.includes(normalizedSearch) || email.includes(normalizedSearch);
    });

    return filtered;
  }, [options, search]);

  const selectedCount = selectedIds.length;

  const selectedPreview = useMemo(() => {
    if (selectedCount === 0) return '';
    const optionsWithId = options.filter(
      (m): m is NonNullable<typeof m> & { id: string } => !!m?.id,
    );
    const byId = new Map(optionsWithId.map((m) => [m.id, m]));
    const names = selectedIds
      .map((id) => byId.get(id))
      .filter((m): m is NonNullable<typeof m> => !!m)
      .map((m) => m.name || m.email || '')
      .filter(Boolean);

    if (names.length === 0) return `${selectedCount} selected`;
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }, [options, selectedCount, selectedIds]);

  const toggleId = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((existing) => existing !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-full justify-between'
          disabled={disabled || !canQuery}
        >
          <span className={cn('truncate', selectedCount === 0 && 'text-muted-foreground')}>
            {selectedCount === 0
              ? canQuery
                ? 'Select recipients (optional)'
                : 'Enter brand code to select recipients'
              : selectedPreview}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[--radix-popover-trigger-width] p-2' align='start'>
        <div className='space-y-2'>
          <Input
            placeholder='Search hotel team...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled || !canQuery}
          />

          {loading && (
            <div className='flex items-center gap-2 text-sm text-muted-foreground px-2 py-1'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading team...
            </div>
          )}

          {!loading && error && (
            <div className='text-sm text-red-500 px-2 py-1'>Failed to load hotel team</div>
          )}

          {!loading && !error && (
            <ScrollArea className='h-56'>
              <div className='space-y-1'>
                {filteredOptions.length === 0 ? (
                  <div className='text-sm text-muted-foreground px-2 py-1'>
                    No team members found
                  </div>
                ) : (
                  filteredOptions.map((m) => {
                    const id = m.id;
                    const isSelected = selectedIds.includes(id);

                    return (
                      <button
                        key={id}
                        type='button'
                        className={cn(
                          'w-full text-left flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
                          isSelected && 'bg-accent text-accent-foreground',
                        )}
                        onClick={() => toggleId(id)}
                        disabled={disabled}
                      >
                        <div className='min-w-0'>
                          <div className='truncate'>{m.name || m.email || 'Team member'}</div>
                          {m.name && m.email && (
                            <div className='truncate text-xs text-muted-foreground'>{m.email}</div>
                          )}
                        </div>
                        <Switch checked={isSelected} onCheckedChange={() => toggleId(id)} />
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          )}

          <div className='flex items-center justify-between px-1 pt-1 text-xs text-muted-foreground'>
            <span>
              {selectedCount === 0 ? 'No recipients selected' : `${selectedCount} selected`}
            </span>
            {selectedCount > 0 && (
              <button
                type='button'
                className='underline underline-offset-2'
                onClick={() => onChange([])}
                disabled={disabled}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
